import { resolveThemeForRuntime, themeTokensToCssVars } from '../chunk-ZR45FGW3.js';
import { useGameAudio, arbitraryClassesOf, SuspenseConfigProvider } from '../chunk-KBT4GP6C.js';
import '../chunk-Q4JDDFCV.js';
import { Box } from '../chunk-MGN6B6OQ.js';
import '../chunk-XJTHMW4G.js';
import '../chunk-KZRAMPM6.js';
import { setGlobalEventBus, useEventBus } from '../chunk-CHNC4UYE.js';
import '../chunk-L3MTZ3RH.js';
import '../chunk-CB64IOYD.js';
import { useOfflineExecutor } from '../chunk-PQKU62LK.js';
import '../chunk-PEST66PN.js';
import '../chunk-N4IJS5DD.js';
import { registerCheck, recordTransition, bindEventBus, bindTraitStateGetter } from '../chunk-N23SVGTH.js';
import { cn } from '../chunk-64WACXM7.js';
import '../chunk-6ZCU3BQN.js';
import '../chunk-FYX64CDL.js';
import '../chunk-QPXIAUJD.js';
import React18, { createContext, useRef, useCallback, useMemo, useEffect, useState, useContext, useSyncExternalStore } from 'react';
import { ThemeProvider, useThemeScope, useTheme, registryKeyTheme, themeHasMode, ThemeScopeContext } from '@almadar/ui/context';
export { DesignThemeProvider, useDesignTheme } from '@almadar/ui/context';
import { createLogger } from '@almadar/logger';
import { jsx, Fragment } from 'react/jsx-runtime';
import { useTranslate } from '@almadar/ui/hooks';
import { ANONYMOUS_USER, isThemeRegistryKey, matchPath, matchPathAmong, defaultPage } from '@almadar/core';
export { ANONYMOUS_USER, comparePathSpecificity, matchPath, matchPathAmong } from '@almadar/core';
import { createHttpTransport } from '@almadar/runtime';
import { useLocation, useNavigate } from 'react-router-dom';
import { matchPathAmong as matchPathAmong$1 } from '@almadar/ui/providers';

var busLog = createLogger("almadar:eventbus");
var subLog = createLogger("almadar:eventbus:subscribe");
var EventBusContext = globalThis.__almadarUiEventBusContext ?? (globalThis.__almadarUiEventBusContext = createContext(null));
var listenerTags = /* @__PURE__ */ new WeakMap();
function captureSubscriberTag(listener) {
  const fnName = listener.name;
  if (typeof fnName === "string" && fnName.length > 0 && fnName !== "listener") {
    return fnName;
  }
  const stack = new Error().stack ?? "";
  const lines = stack.split("\n");
  for (const raw of lines.slice(2)) {
    const line = raw.trim();
    if (!line) continue;
    if (line.includes("EventBusProvider") || line.includes("useEventBus")) continue;
    if (line.includes("captureSubscriberTag")) continue;
    const match = line.match(/([^/\\)]+\.(?:tsx?|jsx?))(?::(\d+))?/);
    if (match) return match[2] ? `${match[1]}:${match[2]}` : match[1];
    return line.slice(0, 120);
  }
  return "unknown";
}
function EventBusProvider({ children, isolated = false }) {
  const listenersRef = useRef(/* @__PURE__ */ new Map());
  const anyListenersRef = useRef(/* @__PURE__ */ new Set());
  const deprecationWarningShown = useRef(false);
  const getSelectedEntity = useCallback(() => {
    if (!deprecationWarningShown.current) {
      busLog.warn("deprecated:getSelectedEntity", {
        migration: "Use SelectionProvider and useSelection hook instead. See SelectionProvider.tsx for migration guide."
      });
      deprecationWarningShown.current = true;
    }
    return null;
  }, []);
  const clearSelectedEntity = useCallback(() => {
    if (!deprecationWarningShown.current) {
      busLog.warn("deprecated:clearSelectedEntity", {
        migration: "Use SelectionProvider and useSelection hook instead. See SelectionProvider.tsx for migration guide."
      });
      deprecationWarningShown.current = true;
    }
  }, []);
  const emit = useCallback((type, payload, source) => {
    const event = {
      type,
      payload,
      timestamp: Date.now(),
      source
    };
    const listeners = listenersRef.current.get(type);
    const listenerCount = (listeners?.size ?? 0) + anyListenersRef.current.size;
    busLog.debug("emit", { type, payloadKeys: payload ? Object.keys(payload).length : 0, listenerCount });
    if (listenerCount === 0) {
      busLog.debug("emit:no-listeners", { type });
    }
    if (listeners) {
      const listenersCopy = Array.from(listeners);
      for (let i = 0; i < listenersCopy.length; i++) {
        const listener = listenersCopy[i];
        busLog.debug("emit:listener", {
          type,
          kind: "specific",
          index: i,
          tag: listenerTags.get(listener) ?? "untagged"
        });
        try {
          listener(event);
        } catch (error) {
          busLog.error("listener-threw", { type, error: error instanceof Error ? error : String(error) });
        }
      }
    }
    const anyListeners = Array.from(anyListenersRef.current);
    for (let i = 0; i < anyListeners.length; i++) {
      const listener = anyListeners[i];
      busLog.debug("emit:listener", {
        type,
        kind: "onAny",
        index: i,
        tag: listenerTags.get(listener) ?? "untagged"
      });
      try {
        listener(event);
      } catch (error) {
        busLog.error("onAny-listener-threw", { type, error: error instanceof Error ? error : String(error) });
      }
    }
  }, []);
  const on = useCallback((type, listener) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, /* @__PURE__ */ new Set());
    }
    const listeners = listenersRef.current.get(type);
    listeners.add(listener);
    if (!listenerTags.has(listener)) listenerTags.set(listener, captureSubscriberTag(listener));
    subLog.debug("subscribe", { type, totalListeners: listeners.size, tag: listenerTags.get(listener) });
    return () => {
      listeners.delete(listener);
      subLog.debug("unsubscribe", { type, remaining: listeners.size });
      if (listeners.size === 0) {
        listenersRef.current.delete(type);
      }
    };
  }, []);
  const once = useCallback((type, listener) => {
    const wrappedListener = (event) => {
      listenersRef.current.get(type)?.delete(wrappedListener);
      listener(event);
    };
    return on(type, wrappedListener);
  }, [on]);
  const hasListeners = useCallback((type) => {
    const listeners = listenersRef.current.get(type);
    return listeners !== void 0 && listeners.size > 0;
  }, []);
  const onAny = useCallback((listener) => {
    anyListenersRef.current.add(listener);
    if (!listenerTags.has(listener)) listenerTags.set(listener, captureSubscriberTag(listener));
    subLog.debug("subscribe:any", { totalAnyListeners: anyListenersRef.current.size, tag: listenerTags.get(listener) });
    return () => {
      anyListenersRef.current.delete(listener);
      subLog.debug("unsubscribe:any", { remaining: anyListenersRef.current.size });
    };
  }, []);
  const contextValue = useMemo(
    () => ({
      emit,
      on,
      once,
      hasListeners,
      onAny,
      getSelectedEntity,
      clearSelectedEntity
    }),
    [emit, on, once, hasListeners, onAny, getSelectedEntity, clearSelectedEntity]
  );
  useEffect(() => {
    if (isolated) return;
    setGlobalEventBus(contextValue);
    return () => {
      setGlobalEventBus(null);
    };
  }, [contextValue, isolated]);
  return /* @__PURE__ */ jsx(EventBusContext.Provider, { value: contextValue, children });
}
var log = createLogger("almadar:ui:selection");
var SelectionContext = createContext(null);
var defaultCompareEntities = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id !== void 0 && a.id === b.id;
};
function SelectionProvider({
  children,
  debug = false,
  compareEntities = defaultCompareEntities
}) {
  const eventBus = useEventBus();
  const [selected, setSelectedState] = useState(null);
  const setSelected = useCallback(
    (entity) => {
      setSelectedState(entity);
      if (debug) {
        log.debug("Selection set", () => ({
          hasEntity: entity !== null && entity !== void 0,
          entityId: entity ? String(entity.id ?? "") : ""
        }));
      }
    },
    [debug]
  );
  const clearSelection = useCallback(() => {
    setSelectedState(null);
    if (debug) {
      log.debug("Selection cleared");
    }
  }, [debug]);
  const isSelected = useCallback(
    (entity) => {
      return selected !== null ? compareEntities(selected, entity) : false;
    },
    [selected, compareEntities]
  );
  useEffect(() => {
    const handleSelect = (event) => {
      const row = event.payload?.row;
      if (row && typeof row === "object" && !Array.isArray(row)) {
        setSelected(row);
        if (debug) {
          log.debug("event received", () => ({
            type: event.type,
            rowId: row && typeof row === "object" ? String(row.id ?? "") : ""
          }));
        }
      }
    };
    const handleDeselect = (event) => {
      clearSelection();
      if (debug) {
        log.debug("event received - clearing selection", { type: event.type });
      }
    };
    const unsubView = eventBus.on("UI:VIEW", handleSelect);
    const unsubSelect = eventBus.on("UI:SELECT", handleSelect);
    const unsubClose = eventBus.on("UI:CLOSE", handleDeselect);
    const unsubDeselect = eventBus.on("UI:DESELECT", handleDeselect);
    const unsubCancel = eventBus.on("UI:CANCEL", handleDeselect);
    return () => {
      unsubView();
      unsubSelect();
      unsubClose();
      unsubDeselect();
      unsubCancel();
    };
  }, [eventBus, setSelected, clearSelection, debug]);
  const contextValue = {
    selected,
    setSelected,
    clearSelection,
    isSelected
  };
  return /* @__PURE__ */ jsx(SelectionContext.Provider, { value: contextValue, children });
}
function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return context;
}
function useSelectionOptional() {
  const context = useContext(SelectionContext);
  return context;
}
var log2 = createLogger("almadar:verify");
var DISPATCH_SUFFIX = ":DISPATCH";
var SUCCESS_SUFFIX = ":SUCCESS";
var ERROR_SUFFIX = ":ERROR";
function parseLifecycleEvent(type) {
  if (type.endsWith(DISPATCH_SUFFIX)) {
    const traitName = type.slice(0, -DISPATCH_SUFFIX.length);
    if (traitName) return { kind: "dispatch", traitName };
  } else if (type.endsWith(SUCCESS_SUFFIX)) {
    const rest = type.slice(0, -SUCCESS_SUFFIX.length);
    const colonIdx = rest.indexOf(":");
    if (colonIdx > 0) {
      return {
        kind: "success",
        traitName: rest.slice(0, colonIdx),
        event: rest.slice(colonIdx + 1)
      };
    }
  } else if (type.endsWith(ERROR_SUFFIX)) {
    const rest = type.slice(0, -ERROR_SUFFIX.length);
    const colonIdx = rest.indexOf(":");
    if (colonIdx > 0) {
      return {
        kind: "error",
        traitName: rest.slice(0, colonIdx),
        event: rest.slice(colonIdx + 1)
      };
    }
  }
  return null;
}
function mapServerEffectResults(effectResults) {
  return effectResults.map((er) => {
    const target = er["entityType"] ?? er["entity"] ?? er["service"];
    const entityName = typeof target === "string" && target !== "" ? target : void 0;
    const rawAction = er["action"];
    const action = rawAction === "create" || rawAction === "update" || rawAction === "delete" || rawAction === "batch" ? rawAction : void 0;
    const data = er["data"];
    const resultId = data !== null && typeof data === "object" && !Array.isArray(data) && typeof data["id"] === "string" ? data["id"] : void 0;
    const denied = er["denied"] === true;
    const success = er["success"] !== false;
    const outcome = denied ? "denied" : success ? "success" : "failed";
    return {
      type: String(er["type"] ?? er["effect"] ?? "server-effect"),
      ...entityName !== void 0 ? { entityName } : {},
      ...action !== void 0 ? { action } : {},
      ...resultId !== void 0 ? { resultId } : {},
      outcome,
      args: entityName !== void 0 ? [entityName] : [],
      status: outcome === "success" ? "executed" : "failed",
      error: er["error"]
    };
  });
}
function VerificationProvider({
  children,
  enabled,
  runtimeManager,
  traitStateGetter
}) {
  const verifyFlag = typeof window !== "undefined" && window.__ALMADAR_VERIFY__ === true;
  const isEnabled = enabled ?? (verifyFlag || typeof process !== "undefined" && process.env?.NODE_ENV !== "production");
  const eventBus = useEventBus();
  const { t } = useTranslate();
  const tRef = useRef(t);
  tRef.current = t;
  const pendingRef = useRef(/* @__PURE__ */ new Map());
  useEffect(() => {
    if (!isEnabled) return;
    if (!eventBus.onAny) return;
    const verificationProviderLifecycleListener = (evt) => {
      const parsed = parseLifecycleEvent(evt.type);
      if (!parsed) return;
      log2.debug("lifecycle:event", { kind: parsed.kind, traitName: parsed.traitName, event: parsed.event, type: evt.type });
      const payload = evt.payload ?? {};
      if (parsed.kind === "dispatch") {
        const key = `${parsed.traitName}:${String(payload["event"] ?? "")}`;
        pendingRef.current.set(key, {
          traitName: parsed.traitName,
          event: String(payload["event"] ?? ""),
          from: payload["currentState"],
          timestamp: evt.timestamp
        });
      } else if (parsed.kind === "success" && parsed.event) {
        const key = `${parsed.traitName}:${parsed.event}`;
        const pending2 = pendingRef.current.get(key);
        pendingRef.current.delete(key);
        const newState = payload["newState"] ?? payload["state"] ?? "unknown";
        const clientEffectsArr = Array.isArray(payload["clientEffects"]) ? payload["clientEffects"] : [];
        const effects = clientEffectsArr.map((e) => ({
          type: String(e["type"] ?? "unknown"),
          args: Array.isArray(e["args"]) ? e["args"] : [],
          status: "executed"
        }));
        const effectResultsRaw = Array.isArray(payload["effectResults"]) ? payload["effectResults"] : [];
        effects.push(...mapServerEffectResults(effectResultsRaw));
        const dataEntities = {};
        const responseData = payload["data"];
        if (responseData && typeof responseData === "object") {
          for (const [entityName, records] of Object.entries(responseData)) {
            dataEntities[entityName] = Array.isArray(records) ? records.length : 0;
          }
        }
        const emittedEventsRaw = Array.isArray(payload["emittedEvents"]) ? payload["emittedEvents"] : [];
        const emittedEvents = emittedEventsRaw.map((e) => typeof e.event === "string" ? e.event : null).filter((e) => e !== null);
        const emitted = emittedEventsRaw.flatMap((e) => {
          if (typeof e.event !== "string") return [];
          const p = e["payload"];
          return [{ event: e.event, ...p !== null && typeof p === "object" && !Array.isArray(p) && { payload: p } }];
        });
        recordTransition({
          traitName: parsed.traitName,
          from: pending2?.from ?? payload["currentState"] ?? newState,
          to: newState,
          event: parsed.event,
          effects,
          serverResponse: {
            orbitalName: parsed.traitName,
            success: true,
            ...typeof payload["transitioned"] === "boolean" && { transitioned: payload["transitioned"] },
            clientEffects: clientEffectsArr.length,
            dataEntities,
            emittedEvents,
            emitted,
            timestamp: Date.now()
          },
          timestamp: Date.now()
        });
        log2.debug("transition:success", { trait: parsed.traitName, event: parsed.event, from: pending2?.from, to: newState, effectCount: effects.length });
      } else if (parsed.kind === "error" && parsed.event) {
        const key = `${parsed.traitName}:${parsed.event}`;
        const pending2 = pendingRef.current.get(key);
        pendingRef.current.delete(key);
        const errorMsg = payload["error"] ?? tRef.current("verification.unknownError");
        const fromState = pending2?.from ?? payload["currentState"] ?? "unknown";
        recordTransition({
          traitName: parsed.traitName,
          from: fromState,
          to: fromState,
          // state didn't change on error
          event: parsed.event,
          effects: [{
            type: "server-call",
            args: [],
            status: "failed",
            error: errorMsg
          }],
          serverResponse: {
            orbitalName: parsed.traitName,
            success: false,
            transitioned: false,
            clientEffects: 0,
            dataEntities: {},
            emittedEvents: [],
            error: errorMsg,
            timestamp: Date.now()
          },
          timestamp: Date.now()
        });
        log2.warn("transition:error", { trait: parsed.traitName, event: parsed.event, from: fromState, error: errorMsg });
      }
    };
    Object.defineProperty(verificationProviderLifecycleListener, "name", {
      value: "VerificationProvider:lifecycle"
    });
    const unsub = eventBus.onAny(verificationProviderLifecycleListener);
    registerCheck(
      "verification-provider",
      "VerificationProvider active (compiled path)",
      "pass"
    );
    return unsub;
  }, [isEnabled, eventBus]);
  useEffect(() => {
    if (!isEnabled) return;
    if (!runtimeManager) return;
    runtimeManager.setObserver({
      onTransition(trace) {
        recordTransition({
          traitName: trace.traitName,
          from: trace.from,
          to: trace.to,
          event: trace.event,
          guardResult: trace.guardResult,
          effects: trace.effects,
          timestamp: Date.now()
        });
      }
    });
    registerCheck(
      "verification-provider",
      "VerificationProvider active (runtime path)",
      "pass"
    );
  }, [isEnabled, runtimeManager]);
  useEffect(() => {
    if (!isEnabled) return;
    bindEventBus(eventBus);
  }, [isEnabled, eventBus]);
  useEffect(() => {
    if (!isEnabled) return;
    if (traitStateGetter) {
      bindTraitStateGetter(traitStateGetter);
    } else if (runtimeManager?.getState) {
      const mgr = runtimeManager;
      bindTraitStateGetter((traitName) => mgr.getState(traitName));
    }
  }, [isEnabled, traitStateGetter, runtimeManager]);
  return /* @__PURE__ */ jsx(Fragment, { children });
}
VerificationProvider.displayName = "VerificationProvider";
var UserContext = createContext(null);
function UserProvider({
  user = null,
  children
}) {
  const hasRole = useCallback(
    (role) => {
      if (!user) return role === "anonymous";
      return user.role === role;
    },
    [user]
  );
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      return user.permissions?.includes(permission) ?? false;
    },
    [user]
  );
  const hasAnyRole = useCallback(
    (roles) => {
      if (!user) return roles.includes("anonymous");
      return user.role ? roles.includes(user.role) : false;
    },
    [user]
  );
  const hasAllPermissions = useCallback(
    (permissions) => {
      if (!user || !user.permissions) return false;
      return permissions.every((p) => user.permissions?.includes(p));
    },
    [user]
  );
  const getUserField = useCallback(
    (path) => {
      const viewer = user ?? ANONYMOUS_USER;
      const [first, ...rest] = path.split(".");
      if (!first) return void 0;
      let value = viewer[first];
      for (const segment of rest) {
        if (value === null || typeof value !== "object" || Array.isArray(value) || value instanceof Date) {
          return void 0;
        }
        value = value[segment];
      }
      return value;
    },
    [user]
  );
  const contextValue = useMemo(
    () => ({
      user,
      isLoggedIn: user !== null,
      hasRole,
      hasPermission,
      hasAnyRole,
      hasAllPermissions,
      getUserField
    }),
    [user, hasRole, hasPermission, hasAnyRole, hasAllPermissions, getUserField]
  );
  return /* @__PURE__ */ jsx(UserContext.Provider, { value: contextValue, children });
}
function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    return {
      user: null,
      isLoggedIn: false,
      hasRole: (role) => role === "anonymous",
      hasPermission: () => false,
      hasAnyRole: (roles) => roles.includes("anonymous"),
      hasAllPermissions: () => false,
      getUserField: () => void 0
    };
  }
  return context;
}
function useHasRole(role) {
  const { hasRole } = useUser();
  return hasRole(role);
}
function useHasPermission(permission) {
  const { hasPermission } = useUser();
  return hasPermission(permission);
}
function useUserForEvaluation() {
  const { user, isLoggedIn } = useUser();
  return isLoggedIn && user ? user : void 0;
}
function OrbitalProvider({
  children,
  themes,
  defaultTheme = "wireframe",
  defaultMode = "system",
  targetRef,
  skipTheme = false,
  debug = false,
  initialData,
  suspense = false,
  verification,
  isolated = false,
  user = null
}) {
  const suspenseConfig = useMemo(
    () => ({ enabled: suspense }),
    [suspense]
  );
  const inner = /* @__PURE__ */ jsx(EventBusProvider, { debug, isolated, children: /* @__PURE__ */ jsx(VerificationProvider, { enabled: verification, children: /* @__PURE__ */ jsx(UserProvider, { user, children: /* @__PURE__ */ jsx(SelectionProvider, { debug, children: /* @__PURE__ */ jsx(SuspenseConfigProvider, { config: suspenseConfig, children }) }) }) }) });
  if (skipTheme) {
    return inner;
  }
  return /* @__PURE__ */ jsx(
    ThemeProvider,
    {
      themes,
      defaultTheme,
      defaultMode,
      targetRef,
      children: inner
    }
  );
}
OrbitalProvider.displayName = "OrbitalProvider";
var SURFACE = "h-full min-h-full bg-background surface-page text-foreground";
function useFontImport(href) {
  useEffect(() => {
    if (!href || typeof document === "undefined") return;
    const existing = Array.from(document.head.querySelectorAll("link[data-almadar-font]")).some((el) => el.getAttribute("href") === href);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-almadar-font", "");
    document.head.appendChild(link);
  }, [href]);
}
function OrbitalThemeProvider({ theme: declared, override, children }) {
  const parent = useThemeScope();
  const hostOverride = override || parent.override || void 0;
  const theme = hostOverride ?? declared;
  const resolved = resolveThemeForRuntime(theme);
  const { resolvedMode, availableThemes } = useTheme();
  const darkTypeScale = resolvedMode === "dark" ? resolved?.variants?.dark?.typeScale : void 0;
  useFontImport(darkTypeScale?.fontImport ?? resolved?.tokens.typeScale?.fontImport);
  if (isThemeRegistryKey(theme)) {
    const keyTheme = registryKeyTheme(theme, availableThemes);
    const mode = keyTheme === void 0 ? parent.mode : themeHasMode(keyTheme.theme, resolvedMode) ? resolvedMode : keyTheme.mode;
    const key = keyTheme === void 0 ? theme : `${keyTheme.theme.name}-${mode}`;
    return /* @__PURE__ */ jsx(
      ThemeScopeContext.Provider,
      {
        value: hostOverride ? { ...parent, theme: key, mode, vars: void 0, override: key } : { ...parent, theme: key, mode },
        children: /* @__PURE__ */ jsx(Box, { "data-theme": key, className: cn(SURFACE, keyTheme && mode), children })
      }
    );
  }
  if (!resolved) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  const vars = themeTokensToCssVars(resolved.tokens, resolvedMode, resolved.variants?.dark);
  return /* @__PURE__ */ jsx(ThemeScopeContext.Provider, { value: { ...parent, vars: { ...parent.vars, ...vars } }, children: /* @__PURE__ */ jsx(Box, { "data-orbital-theme": resolved.name, className: SURFACE, style: vars, children }) });
}
OrbitalThemeProvider.displayName = "OrbitalThemeProvider";
var CurrentPagePathContext = createContext(void 0);
var CurrentPagePathProvider = ({
  value,
  children
}) => /* @__PURE__ */ jsx(CurrentPagePathContext.Provider, { value, children });
CurrentPagePathProvider.displayName = "CurrentPagePathProvider";
var useCurrentPagePath = () => useContext(CurrentPagePathContext);
var RenderSlotContext = createContext("main");
var RenderSlotProvider = ({ slot, children }) => /* @__PURE__ */ jsx(RenderSlotContext.Provider, { value: slot, children });
RenderSlotProvider.displayName = "RenderSlotProvider";
function useRenderSlot() {
  return useContext(RenderSlotContext);
}
var EntitySchemaContext = createContext(null);
function EntitySchemaProvider({
  entities,
  traitLinkedEntities,
  orbitalsByTrait,
  children
}) {
  const entitiesMap = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const entity of entities) {
      map.set(entity.name, entity);
    }
    return map;
  }, [entities]);
  const linkedMap = useMemo(() => {
    return traitLinkedEntities ?? /* @__PURE__ */ new Map();
  }, [traitLinkedEntities]);
  const orbitalsMap = useMemo(() => {
    return orbitalsByTrait ?? /* @__PURE__ */ new Map();
  }, [orbitalsByTrait]);
  const contextValue = useMemo(
    () => ({
      entities: entitiesMap,
      traitLinkedEntities: linkedMap,
      orbitalsByTrait: orbitalsMap
    }),
    [entitiesMap, linkedMap, orbitalsMap]
  );
  return /* @__PURE__ */ jsx(EntitySchemaContext.Provider, { value: contextValue, children });
}
function useEntitySchema() {
  const context = useContext(EntitySchemaContext);
  if (!context) {
    throw new Error("useEntitySchema must be used within an EntitySchemaProvider");
  }
  return context;
}
function useEntitySchemaOptional() {
  return useContext(EntitySchemaContext);
}
var log3 = createLogger("almadar:ui:navigation");
function extractRouteParams(pattern, path) {
  return matchPath(pattern, path) || {};
}
function pathMatches(pattern, path) {
  return matchPath(pattern, path) !== null;
}
function isInlineOrbital(orbital) {
  return "name" in orbital && typeof orbital.name === "string";
}
function isInlinePage(page) {
  return typeof page === "object" && page !== null && "name" in page && typeof page.name === "string";
}
function findPageByPath(schema, path) {
  if (!schema.orbitals) return null;
  const candidates = [];
  for (const orbital of schema.orbitals) {
    if (!isInlineOrbital(orbital)) continue;
    if (!orbital.pages) continue;
    for (const pageRef of orbital.pages) {
      if (!isInlinePage(pageRef)) continue;
      candidates.push({ page: pageRef, orbitalName: orbital.name });
    }
  }
  const hit = matchPathAmong(candidates, path, (entry) => entry.page.path);
  if (!hit) return null;
  return {
    page: hit.candidate.page,
    params: hit.params,
    orbitalName: hit.candidate.orbitalName
  };
}
function findPageByName(schema, pageName) {
  if (!schema.orbitals) return null;
  for (const orbital of schema.orbitals) {
    if (!isInlineOrbital(orbital)) continue;
    if (!orbital.pages) continue;
    for (const pageRef of orbital.pages) {
      if (!isInlinePage(pageRef)) continue;
      const page = pageRef;
      if (page.name === pageName) {
        return { page, orbitalName: orbital.name };
      }
    }
  }
  return null;
}
function getDefaultPage(schema) {
  return defaultPage(schema);
}
function getAllPages(schema) {
  const pages = [];
  if (!schema.orbitals) return pages;
  for (const orbital of schema.orbitals) {
    if (!isInlineOrbital(orbital)) continue;
    if (!orbital.pages) continue;
    for (const pageRef of orbital.pages) {
      if (isInlinePage(pageRef)) {
        pages.push({ page: pageRef, orbitalName: orbital.name });
      }
    }
  }
  return pages;
}
var NavigationContext = createContext(null);
function NavigationProvider({
  schema,
  initialPage,
  updateUrl = true,
  onNavigate,
  children
}) {
  const initialState = useMemo(() => {
    let page = null;
    let path = "/";
    if (initialPage) {
      const found = findPageByName(schema, initialPage);
      if (found) {
        page = found.page;
        path = page.path || "/";
      }
    }
    if (!page) {
      const defaultPage2 = getDefaultPage(schema);
      if (defaultPage2) {
        page = defaultPage2.page;
        path = page.path || "/";
      }
    }
    return {
      activePage: page?.name || "",
      currentPath: path,
      initPayload: {},
      navigationId: 0
    };
  }, [schema, initialPage]);
  const [state, setState] = useState(initialState);
  const navigateTo = useCallback((path, payload) => {
    const result = findPageByPath(schema, path);
    if (!result) {
      log3.error("No page found for path", { path });
      return;
    }
    const { page, params } = result;
    const finalPayload = { ...params, ...payload };
    log3.debug("Navigating to", () => ({
      path,
      page: page.name,
      params,
      payload: JSON.stringify(payload),
      finalPayload: JSON.stringify(finalPayload)
    }));
    setState((prev) => ({
      activePage: page.name,
      currentPath: path,
      initPayload: finalPayload,
      navigationId: prev.navigationId + 1
    }));
    if (updateUrl && typeof window !== "undefined") {
      try {
        window.history.pushState(finalPayload, "", path);
      } catch (e) {
        log3.warn("Could not update URL", { error: e instanceof Error ? e : String(e) });
      }
    }
    if (onNavigate) {
      onNavigate(page.name, path, finalPayload);
    }
  }, [schema, updateUrl, onNavigate]);
  const navigateToPage = useCallback((pageName, payload) => {
    const result = findPageByName(schema, pageName);
    if (!result) {
      log3.error("No page found with name", { pageName });
      return;
    }
    const { page } = result;
    const path = page.path || `/${pageName.toLowerCase()}`;
    log3.debug("Navigating to page", () => ({
      pageName,
      path,
      payload: JSON.stringify(payload)
    }));
    setState((prev) => ({
      activePage: page.name,
      currentPath: path,
      initPayload: payload || {},
      navigationId: prev.navigationId + 1
    }));
    if (updateUrl && typeof window !== "undefined") {
      try {
        window.history.pushState(payload || {}, "", path);
      } catch (e) {
        log3.warn("Could not update URL", { error: e instanceof Error ? e : String(e) });
      }
    }
    if (onNavigate) {
      onNavigate(page.name, path, payload || {});
    }
  }, [schema, updateUrl, onNavigate]);
  const contextValue = useMemo(() => ({
    state,
    navigateTo,
    navigateToPage,
    schema,
    isReady: !!state.activePage
  }), [state, navigateTo, navigateToPage, schema]);
  return /* @__PURE__ */ jsx(NavigationContext.Provider, { value: contextValue, children });
}
function useNavigation() {
  return useContext(NavigationContext);
}
function useNavigateTo() {
  const context = useContext(NavigationContext);
  const noOp = useCallback((path, _payload) => {
    log3.warn("navigateTo called outside NavigationProvider", { path });
  }, []);
  return context?.navigateTo || noOp;
}
function useNavigationState() {
  const context = useContext(NavigationContext);
  return context?.state || null;
}
function useInitPayload() {
  const context = useContext(NavigationContext);
  return context?.state.initPayload || {};
}
function useActivePage() {
  const context = useContext(NavigationContext);
  return context?.state.activePage || null;
}
function useNavigationId() {
  const context = useContext(NavigationContext);
  return context?.state.navigationId || 0;
}
var ServerBridgeContext = createContext(null);
function useServerBridge() {
  const ctx = useContext(ServerBridgeContext);
  if (!ctx) {
    return {
      connected: false,
      carriesCircuitState: false,
      topologyKnown: false,
      transport: {
        async register() {
          return { success: false, carriesCircuitState: false };
        },
        async unregister() {
        },
        async send() {
          throw new Error("useServerBridge: no ServerBridgeProvider in the component tree");
        }
      }
    };
  }
  return ctx;
}
function ServerBridgeProvider({
  schema,
  serverUrl,
  transport: customTransport,
  getAccessToken,
  children
}) {
  if (!serverUrl && !customTransport) {
    throw new Error("ServerBridgeProvider requires either serverUrl or transport");
  }
  if (serverUrl && customTransport) {
    throw new Error("ServerBridgeProvider accepts serverUrl OR transport, not both");
  }
  const [connected, setConnected] = useState(false);
  const [carriesCircuitState, setCarriesCircuitState] = useState(false);
  const [topologyKnown, setTopologyKnown] = useState(false);
  const transport = useMemo(
    () => customTransport ?? createHttpTransport({ serverUrl, getAccessToken }),
    [serverUrl, customTransport, getAccessToken]
  );
  const registerSchema = useCallback(
    async () => transport.register(schema),
    [schema, transport]
  );
  const unregisterSchema = useCallback(
    async () => transport.unregister(),
    [transport]
  );
  useEffect(() => {
    if (!schema) return;
    let cancelled = false;
    registerSchema().then((result) => {
      if (cancelled) return;
      setConnected(result.success);
      setCarriesCircuitState(result.carriesCircuitState);
      setTopologyKnown(true);
    });
    return () => {
      cancelled = true;
      setConnected(false);
      setTopologyKnown(false);
      unregisterSchema();
    };
  }, [schema, registerSchema, unregisterSchema]);
  return /* @__PURE__ */ jsx(ServerBridgeContext.Provider, { value: { connected, carriesCircuitState, topologyKnown, transport }, children });
}
var log4 = createLogger("almadar:ui:trait-provider");
var TraitContext = createContext(null);
function TraitProvider({
  traits: traitBindings,
  children
}) {
  const traitInstances = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const binding of traitBindings) {
      const trait = binding.trait;
      const initialState = trait.states.find((s) => s.isInitial);
      const stateName = initialState?.name || trait.states[0]?.name || "idle";
      const instance = {
        name: trait.name,
        currentState: stateName,
        availableEvents: trait.transitions.filter((t) => t.from === stateName).map((t) => t.event),
        dispatch: (eventKey, payload) => {
          log4.debug("Dispatch", () => ({
            trait: trait.name,
            event: eventKey,
            payloadKeys: payload ? Object.keys(payload) : []
          }));
        },
        canDispatch: (eventKey) => {
          return trait.transitions.some(
            (t) => t.from === stateName && t.event === eventKey
          );
        },
        trait
      };
      map.set(trait.name, instance);
    }
    return map;
  }, [traitBindings]);
  const contextValue = useMemo(() => {
    return {
      traits: traitInstances,
      getTrait: (name) => traitInstances.get(name),
      dispatchToTrait: (traitName, eventKey, payload) => {
        const instance = traitInstances.get(traitName);
        if (instance) {
          instance.dispatch(eventKey, payload);
        }
      },
      canDispatch: (traitName, eventKey) => {
        const instance = traitInstances.get(traitName);
        return instance?.canDispatch(eventKey) || false;
      }
    };
  }, [traitInstances]);
  return /* @__PURE__ */ jsx(TraitContext.Provider, { value: contextValue, children });
}
function useTraitContext() {
  const context = useContext(TraitContext);
  if (!context) {
    throw new Error("useTraitContext must be used within a TraitProvider");
  }
  return context;
}
function useTrait(traitName) {
  const context = useTraitContext();
  return context.getTrait(traitName);
}
var EMPTY_CHAIN = Object.freeze([]);
var TraitScopeContext = createContext(null);
function TraitScopeProvider({
  orbital,
  trait,
  children
}) {
  const parentChain = useContext(TraitScopeContext);
  const value = useMemo(() => {
    const self = { orbital, trait };
    return parentChain && parentChain.length > 0 ? [self, ...parentChain] : [self];
  }, [orbital, trait, parentChain]);
  return /* @__PURE__ */ jsx(TraitScopeContext.Provider, { value, children });
}
function useTraitScopeChain() {
  const chain = useContext(TraitScopeContext);
  return chain ?? EMPTY_CHAIN;
}
function useTraitScope() {
  const chain = useContext(TraitScopeContext);
  return chain && chain.length > 0 ? chain[0] : null;
}
var EntityBindingContext = createContext(null);
var EMPTY_ENTITY = {};
var NOOP_SUBSCRIBE = () => () => void 0;
function useEntityBindingSnapshot(traitName) {
  const source = useContext(EntityBindingContext);
  const entity = useSyncExternalStore(
    source !== null && traitName !== void 0 ? (onStoreChange) => source.subscribe(traitName, onStoreChange) : NOOP_SUBSCRIBE,
    () => source !== null && traitName !== void 0 ? source.getEntitySnapshot(traitName) : EMPTY_ENTITY
  );
  const config = useMemo(
    () => source !== null && traitName !== void 0 ? source.getConfig(traitName) : void 0,
    [source, traitName]
  );
  return {
    entity,
    config,
    state: source !== null && traitName !== void 0 ? source.getState(traitName) : ""
  };
}
var AwaitingSkeletonContext = createContext(null);
var NOOP_SUBSCRIBE2 = () => () => void 0;
var ZERO = () => 0;
function useAwaitingSkeleton(target) {
  const source = useContext(AwaitingSkeletonContext);
  useSyncExternalStore(source?.subscribe ?? NOOP_SUBSCRIBE2, source?.getVersion ?? ZERO, ZERO);
  if (source === null) return void 0;
  return "slot" in target ? source.forSlot(target.slot) : source.forTrait(target.trait);
}
var OfflineModeContext = createContext(null);
function OfflineModeProvider({
  children,
  ...executorOptions
}) {
  const [forceOffline, setForceOffline] = useState(false);
  const executor = useOfflineExecutor(executorOptions);
  const effectivelyOffline = executor.isOffline || forceOffline;
  const contextValue = useMemo(
    () => ({
      ...executor,
      forceOffline,
      setForceOffline,
      effectivelyOffline
    }),
    [executor, forceOffline, effectivelyOffline]
  );
  return /* @__PURE__ */ jsx(OfflineModeContext.Provider, { value: contextValue, children });
}
function useOfflineMode() {
  const context = useContext(OfflineModeContext);
  if (!context) {
    throw new Error("useOfflineMode must be used within OfflineModeProvider");
  }
  return context;
}
function useOptionalOfflineMode() {
  return useContext(OfflineModeContext);
}
var GameAudioContext = createContext(null);
GameAudioContext.displayName = "GameAudioContext";
function useGameAudioContext() {
  const ctx = useContext(GameAudioContext);
  if (!ctx) {
    throw new Error("useGameAudioContext must be used inside <GameAudioProvider>");
  }
  return ctx;
}
function useGameAudioContextOptional() {
  return useContext(GameAudioContext);
}
function GameAudioProvider({
  manifest,
  baseUrl = "",
  children,
  initialMuted = false
}) {
  const eventBus = useEventBus();
  const { play, stop, stopAll, playMusic, stopMusic, muted, setMuted, masterVolume, setMasterVolume } = useGameAudio({ manifest, baseUrl, initialMuted });
  useEffect(() => {
    const unsubPlay = eventBus.on("UI:PLAY_SOUND", (event) => {
      const key = event.payload?.key;
      if (key) play(key);
    });
    const unsubStop = eventBus.on("UI:STOP_SOUND", (event) => {
      const key = event.payload?.key;
      if (key) {
        stop(key);
      } else {
        stopAll();
      }
    });
    const unsubChangeMusic = eventBus.on("UI:CHANGE_MUSIC", (event) => {
      const key = event.payload?.key;
      if (key) {
        playMusic(key);
      } else {
        stopMusic();
      }
    });
    const unsubStopMusic = eventBus.on("UI:STOP_MUSIC", () => {
      stopMusic();
    });
    return () => {
      unsubPlay();
      unsubStop();
      unsubChangeMusic();
      unsubStopMusic();
    };
  }, [eventBus, play, stop, stopAll, playMusic, stopMusic]);
  const value = { muted, setMuted, masterVolume, setMasterVolume, play, playMusic, stopMusic };
  return /* @__PURE__ */ jsx(GameAudioContext.Provider, { value, children });
}
GameAudioProvider.displayName = "GameAudioProvider";
function PageAccessHost({
  access,
  authority,
  viewerId,
  signIn,
  path,
  onDenied,
  children
}) {
  const gated = access === "authenticated";
  const denied = gated && authority !== "pending" && authority !== "authenticated";
  useEffect(() => {
    if (denied && signIn !== void 0) onDenied(signIn, path);
  }, [denied, signIn, path, onDenied]);
  if (!gated) return /* @__PURE__ */ jsx(Fragment, { children });
  if (authority !== "authenticated") return null;
  return /* @__PURE__ */ jsx(React18.Fragment, { children }, viewerId);
}
PageAccessHost.displayName = "PageAccessHost";
function pageEntryLabel(page, navLabels) {
  return page.label ?? navLabels?.[page.path] ?? page.name;
}
function navLabelsFromItems(pages, items) {
  const labels = {};
  const visit = (list) => {
    for (const item of list) {
      const page = matchNavPage(pages, item.href);
      if (page && item.label) labels[page.path] = item.label;
      if (item.children) visit(item.children);
    }
  };
  visit(items);
  return labels;
}
function resolveEntryLabels(entries, pages, navLabels) {
  let changed = false;
  const resolved = entries.map((entry) => {
    const page = matchNavPage(pages, entry.href);
    if (!page || entry.label !== pageEntryLabel(page)) return entry;
    const label = pageEntryLabel(page, navLabels);
    if (label === entry.label) return entry;
    changed = true;
    return { ...entry, label };
  });
  return changed ? resolved : entries;
}
function normalizePath(p) {
  let normalized = p.trim();
  if (!normalized.startsWith("/")) normalized = "/" + normalized;
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}
function matchNavPage(pages, path) {
  const hit = matchPathAmong$1(pages, path, (p) => p.path);
  return hit ? hit.candidate : null;
}
function seedAncestors(pages, path) {
  const segments = normalizePath(path).split("/").filter(Boolean);
  const ancestors = [];
  for (let depth = 1; depth < segments.length; depth++) {
    const prefix = "/" + segments.slice(0, depth).join("/");
    const page = matchNavPage(pages, prefix);
    if (page) {
      ancestors.push({ href: prefix, label: pageEntryLabel(page) });
    }
  }
  return ancestors;
}
function syncNavStack(state, pages, path, pending2) {
  const normalized = normalizePath(path);
  const page = matchNavPage(pages, normalized);
  if (!page) return { state, orbital: null };
  const label = pending2 && normalizePath(pending2.href) === normalized ? pending2.crumb : pageEntryLabel(page);
  const prior = state[page.orbital];
  const stack = prior && prior.length > 0 ? [...prior] : seedAncestors(pages, normalized);
  const existing = stack.findIndex((e) => e.href === normalized);
  if (existing >= 0) {
    stack.length = existing + 1;
    if (pending2 && normalizePath(pending2.href) === normalized) {
      stack[existing] = { href: normalized, label };
    }
  } else {
    stack.push({ href: normalized, label });
  }
  return { state: { ...state, [page.orbital]: stack }, orbital: page.orbital };
}
function relabelCurrent(state, pages, path, label) {
  const normalized = normalizePath(path);
  const page = matchNavPage(pages, normalized);
  if (!page || !label) return state;
  const stack = state[page.orbital];
  if (!stack || stack.length === 0) return state;
  const idx = stack.findIndex((e) => e.href === normalized);
  if (idx < 0 || stack[idx].label === label) return state;
  const next = [...stack];
  next[idx] = { href: normalized, label };
  return { ...state, [page.orbital]: next };
}
function previousEntry(state, pages, path) {
  const page = matchNavPage(pages, normalizePath(path));
  if (!page) return null;
  const stack = state[page.orbital] ?? [];
  return stack.length >= 2 ? stack[stack.length - 2] : null;
}
function entriesFor(state, pages, path) {
  const page = matchNavPage(pages, normalizePath(path));
  if (!page) return [];
  return state[page.orbital] ?? [];
}
var INERT_API = {
  entries: [],
  canGoBack: false,
  beginNavigate: () => void 0,
  back: () => void 0,
  goTo: () => void 0,
  setCurrentLabel: () => void 0,
  registerNavItems: () => void 0
};
var NavStackContext = createContext(INERT_API);
function isInertNavStack(api) {
  return api === INERT_API;
}
function useNavStack() {
  return useContext(NavStackContext);
}
function loadStored(storageKey) {
  if (!storageKey) return {};
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
}
function persistStored(storageKey, state) {
  if (!storageKey) return;
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
  }
}
function sameNavItems(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i];
    return item.href === other.href && item.label === other.label && sameNavItems(item.children ?? [], other.children ?? []);
  });
}
var NavStackProvider = ({
  pages,
  currentPath,
  navigate,
  storageKey,
  children
}) => {
  const [state, setState] = useState(() => loadStored(storageKey));
  const [navItems, setNavItems] = useState([]);
  const pendingCrumbRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    const pending2 = pendingCrumbRef.current;
    pendingCrumbRef.current = null;
    setState((prev) => {
      const next = syncNavStack(prev, pages, currentPath, pending2);
      if (next.state !== prev) persistStored(storageKey, next.state);
      return next.state;
    });
  }, [currentPath, pages, storageKey]);
  const beginNavigate = useCallback((href, crumb) => {
    pendingCrumbRef.current = crumb !== void 0 && crumb !== "" ? { href, crumb } : null;
  }, []);
  const back = useCallback(() => {
    const prev = previousEntry(stateRef.current, pages, currentPath);
    if (prev) navigate(prev.href);
  }, [pages, currentPath, navigate]);
  const goTo = useCallback(
    (href) => {
      navigate(href);
    },
    [navigate]
  );
  const setCurrentLabel = useCallback(
    (label) => {
      setState((prev) => {
        const next = relabelCurrent(prev, pages, currentPath, label);
        if (next !== prev) persistStored(storageKey, next);
        return next;
      });
    },
    [pages, currentPath, storageKey]
  );
  const registerNavItems = useCallback((items) => {
    setNavItems((prev) => sameNavItems(prev, items) ? prev : items);
  }, []);
  const navLabels = useMemo(() => navLabelsFromItems(pages, navItems), [pages, navItems]);
  const entries = useMemo(
    () => resolveEntryLabels(entriesFor(state, pages, currentPath), pages, navLabels),
    [state, pages, currentPath, navLabels]
  );
  const api = useMemo(
    () => ({
      entries,
      canGoBack: previousEntry(state, pages, currentPath) !== null,
      beginNavigate,
      back,
      goTo,
      setCurrentLabel,
      registerNavItems
    }),
    [entries, state, pages, currentPath, beginNavigate, back, goTo, setCurrentLabel, registerNavItems]
  );
  return /* @__PURE__ */ jsx(NavStackContext.Provider, { value: api, children });
};
NavStackProvider.displayName = "NavStackProvider";
var NavStackRouterBridge = ({
  pages,
  storageKey = "almadar:navstack",
  children
}) => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const navigate = useCallback(
    (path) => {
      routerNavigate(path);
    },
    [routerNavigate]
  );
  const eventBus = useEventBus();
  useEffect(
    () => eventBus.on("UI:NAVIGATE", (event) => {
      const url = event.payload?.url;
      if (typeof url === "string" && url.length > 0) routerNavigate(url);
    }),
    [eventBus, routerNavigate]
  );
  return /* @__PURE__ */ jsx(
    NavStackProvider,
    {
      pages,
      currentPath: location.pathname,
      navigate,
      storageKey,
      children
    }
  );
};
NavStackRouterBridge.displayName = "NavStackRouterBridge";
var log5 = createLogger("almadar:ui:arbitrary-classes");
var ARBITRARY_CLASS_STYLE_ID = "almadar-arbitrary-classes";
var CompilerContext = createContext(null);
function ArbitraryClassCompilerProvider({ compile, children }) {
  return /* @__PURE__ */ jsx(CompilerContext.Provider, { value: compile, children });
}
var compiled = /* @__PURE__ */ new Set();
var pending = /* @__PURE__ */ new Set();
function appendCss(css) {
  let el = document.getElementById(ARBITRARY_CLASS_STYLE_ID);
  if (!el) {
    el = document.createElement("style");
    el.id = ARBITRARY_CLASS_STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = `${el.textContent ?? ""}${css}
`;
}
function resetArbitraryClassStyles() {
  compiled.clear();
  pending.clear();
  document.getElementById(ARBITRARY_CLASS_STYLE_ID)?.remove();
}
function useArbitraryClassStyles(schema) {
  const compile = useContext(CompilerContext);
  const classes = useMemo(() => schema ? arbitraryClassesOf(schema) : [], [schema]);
  useEffect(() => {
    if (!compile) return;
    const missing = classes.filter((c) => !compiled.has(c) && !pending.has(c));
    if (missing.length === 0) return;
    missing.forEach((c) => pending.add(c));
    compile(missing).then((css) => {
      missing.forEach((c) => {
        pending.delete(c);
        compiled.add(c);
      });
      if (css) appendCss(css);
    }).catch((err) => {
      missing.forEach((c) => pending.delete(c));
      log5.warn("compile:failed", { classes: missing, error: err instanceof Error ? err.message : String(err) });
    });
  }, [compile, classes]);
}
var DEFAULT_CONFIG = {
  modelOverrides: {},
  effectsEnabled: true,
  palette: null
};
var Avl3DContext = createContext(DEFAULT_CONFIG);
function useAvl3DConfig() {
  return useContext(Avl3DContext);
}
function useAvl3DPalette() {
  return useContext(Avl3DContext).palette;
}
var SurfaceContext = React18.createContext(false);
function SurfaceBoundary({ children }) {
  return /* @__PURE__ */ jsx(SurfaceContext.Provider, { value: true, children });
}
function useContentSurface(surface = "auto") {
  const onSurface = React18.useContext(SurfaceContext);
  const paints = surface === "auto" && !onSurface;
  return {
    className: paints ? "surface-content" : void 0,
    provide: (node) => paints ? /* @__PURE__ */ jsx(SurfaceBoundary, { children: node }) : /* @__PURE__ */ jsx(Fragment, { children: node })
  };
}
function useOnSurface() {
  return React18.useContext(SurfaceContext);
}

export { ARBITRARY_CLASS_STYLE_ID, ArbitraryClassCompilerProvider, Avl3DContext, AwaitingSkeletonContext, CurrentPagePathContext, CurrentPagePathProvider, EntityBindingContext, EntitySchemaProvider, EventBusContext, EventBusProvider, GameAudioContext, GameAudioProvider, NavStackProvider, NavStackRouterBridge, NavigationProvider, OfflineModeProvider, OrbitalProvider, OrbitalThemeProvider, PageAccessHost, RenderSlotProvider, SelectionContext, SelectionProvider, ServerBridgeProvider, SurfaceBoundary, TraitContext, TraitProvider, TraitScopeProvider, UserContext, UserProvider, VerificationProvider, extractRouteParams, findPageByName, findPageByPath, getAllPages, getDefaultPage, isInertNavStack, mapServerEffectResults, pathMatches, resetArbitraryClassStyles, useActivePage, useArbitraryClassStyles, useAvl3DConfig, useAvl3DPalette, useAwaitingSkeleton, useContentSurface, useCurrentPagePath, useEntityBindingSnapshot, useEntitySchema, useEntitySchemaOptional, useGameAudioContext, useGameAudioContextOptional, useHasPermission, useHasRole, useInitPayload, useNavStack, useNavigateTo, useNavigation, useNavigationId, useNavigationState, useOfflineMode, useOnSurface, useOptionalOfflineMode, useRenderSlot, useSelection, useSelectionOptional, useServerBridge, useTrait, useTraitContext, useTraitScope, useTraitScopeChain, useUser, useUserForEvaluation };
